import type { PaidPlan, PlanLeg } from '@/lib/paid-plan/types';

/**
 * The person's target cost per result for one saved campaign. It is stored on the plan's leg for that
 * campaign (`PlanLeg.targetCostMinor`, inside the existing `legs` JSON column, so no migration), because
 * a plan saved on two channels is two campaigns and a target for one is not a target for the other.
 *
 * Read here and nowhere else. Absent, zero or not a whole number of minor units reads as no target: a
 * target is never invented and an unread one is never zero.
 */

export function legTargetMinor(
  leg: Pick<PlanLeg, 'targetCostMinor'> | null | undefined,
): number | null {
  const value = leg?.targetCostMinor;
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * The target for the campaign saved from `draftId`, in the major unit. A plan with no draft named, or no
 * leg for it, has none.
 */
export function planTargetCost(
  plan: Pick<PaidPlan, 'legs'> | null | undefined,
  draftId?: string | null,
): number | null {
  if (!plan || !draftId) return null;
  const minor = legTargetMinor(plan.legs?.find((leg) => leg.draftId === draftId));
  return minor === null ? null : minor / 100;
}

/** The most a target can be, in the major unit: a typo guard, not a limit on what a result is worth. */
export const MAX_TARGET_MAJOR = 100_000;

export type TargetInput = { ok: true; minor: number } | { ok: false; reason: string };

export const TARGET_INPUT_REASON = 'Type an amount above zero, for example 40 or 40.50.';

/** A typed amount in the major unit, as minor units. Rejects empty, zero, negative and non-numbers. */
export function parseTargetInput(text: string): TargetInput {
  const cleaned = text.replace(/[\s,$]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return { ok: false, reason: TARGET_INPUT_REASON };
  const major = Number(cleaned);
  if (!Number.isFinite(major) || major <= 0 || major > MAX_TARGET_MAJOR) {
    return { ok: false, reason: TARGET_INPUT_REASON };
  }
  return { ok: true, minor: Math.round(major * 100) };
}

/** What the input shows for a stored target: "40" or "40.50". */
export function targetInputValue(minor: number | null): string {
  if (minor === null) return '';
  const major = minor / 100;
  return Number.isInteger(major) ? String(major) : major.toFixed(2);
}
